const FONT_SCALE_COOKIE = "ifag_accessibility_font_scale";
const HIGH_CONTRAST_COOKIE = "ifag_accessibility_high_contrast";
const FONT_SCALE_CLASS_PREFIX = "ifag-accessibility-theme--font-";
const HIGH_CONTRAST_CLASS = "ifag-accessibility-theme--high-contrast";
const FONT_SCALE_STEP = 10;
const MIN_FONT_SCALE = 80;
const MAX_FONT_SCALE = 200;
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const setCookie = (name, value) => {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
};

const getCurrentFontScale = html => {
  const fontScaleClass = Array.from(html.classList).find(className => className.startsWith(FONT_SCALE_CLASS_PREFIX));
  const fontScale = Number(fontScaleClass?.replace(FONT_SCALE_CLASS_PREFIX, ""));

  return Number.isInteger(fontScale) && fontScale >= MIN_FONT_SCALE && fontScale <= MAX_FONT_SCALE ? fontScale : 100;
};

const setFontScale = (html, requestedFontScale) => {
  const fontScale = Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, requestedFontScale));

  Array.from(html.classList)
    .filter(className => className.startsWith(FONT_SCALE_CLASS_PREFIX))
    .forEach(className => html.classList.remove(className));

  html.classList.add(`${FONT_SCALE_CLASS_PREFIX}${fontScale}`);
  setCookie(FONT_SCALE_COOKIE, fontScale);
};

const getFixedElementHeight = selector => {
  const element = document.querySelector(selector);

  if (!element || getComputedStyle(element).position !== "fixed") {
    return 0;
  }

  return element.offsetHeight;
};

const getFixedOffset = () => {
  return getFixedElementHeight(".ifag-accessibility") + getFixedElementHeight(".header-ifag");
};

const isInsideFixedElement = element => {
  let currentElement = element;

  while (currentElement && currentElement !== document.body) {
    if (getComputedStyle(currentElement).position === "fixed") {
      return true;
    }

    currentElement = currentElement.parentElement;
  }

  return false;
};

const focusAndScroll = (target, elementToFocus = target) => {
  if (!target || !elementToFocus) {
    return;
  }

  elementToFocus.focus({ preventScroll: true });

  if (isInsideFixedElement(target)) {
    return;
  }

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const top = target.getBoundingClientRect().top + window.scrollY - getFixedOffset();

  window.scrollTo({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    top: Math.max(0, top),
  });
};

const afterLayoutUpdate = callback => {
  requestAnimationFrame(callback);
};

const openMobileMenu = () => {
  const nav = document.getElementById("ifag-main-menu");

  document.dispatchEvent(new CustomEvent("ifag:open-menu"));

  return nav;
};

export const initAccessibility = () => {
  const accessibility = document.querySelector(".ifag-accessibility");

  if (!accessibility) {
    return;
  }

  const html = document.documentElement;
  const linksToggle = accessibility.querySelector(".ifag-accessibility__toggle");
  const contrastToggle = accessibility.querySelector("[data-accessibility-action='toggle-contrast']");
  const closeLinksPanel = () => {
    accessibility.classList.remove("ifag-accessibility--open");
    linksToggle?.setAttribute("aria-expanded", "false");
  };

  linksToggle?.addEventListener("click", () => {
    const isOpen = accessibility.classList.toggle("ifag-accessibility--open");
    linksToggle.setAttribute("aria-expanded", String(isOpen));
  });

  accessibility.querySelector("[data-accessibility-action='increase-font']")?.addEventListener("click", () => {
    setFontScale(html, getCurrentFontScale(html) + FONT_SCALE_STEP);
  });

  accessibility.querySelector("[data-accessibility-action='decrease-font']")?.addEventListener("click", () => {
    setFontScale(html, getCurrentFontScale(html) - FONT_SCALE_STEP);
  });

  accessibility.querySelector("[data-accessibility-action='reset-font']")?.addEventListener("click", () => {
    setFontScale(html, 100);
  });

  contrastToggle?.addEventListener("click", () => {
    const isHighContrast = html.classList.toggle(HIGH_CONTRAST_CLASS);

    contrastToggle.setAttribute("aria-pressed", String(isHighContrast));
    setCookie(HIGH_CONTRAST_COOKIE, isHighContrast);
  });

  accessibility.querySelectorAll("[data-accessibility-target]").forEach(link => {
    link.addEventListener("click", event => {
      event.preventDefault();
      closeLinksPanel();

      if (link.dataset.accessibilityAction === "focus-menu") {
        const nav = openMobileMenu();
        afterLayoutUpdate(() => focusAndScroll(nav));
        return;
      }

      if (link.dataset.accessibilityAction === "focus-search") {
        document.dispatchEvent(new CustomEvent("ifag:open-search", { detail: { trigger: link } }));
        return;
      }

      const target = document.getElementById(link.dataset.accessibilityTarget);
      afterLayoutUpdate(() => focusAndScroll(target));
    });
  });
};
